import React, { useMemo, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { isEqual } from 'lodash';
import {
  clearAllFilters,
  removeFilter,
  selectFilters,
  setDefaultTier3Filters,
  setMainDealerId,
} from '../../redux/filtersReducer';
import { selectAppConfig } from '../../redux/appConfigReducer';
import {
  FilterTag,
  FilterTagWrapper,
  Label,
  IconBox,
  ResetFiltersCTA,
  FilterCount,
  ResetLabel,
  RightShadow,
  TagsWrapper,
  LeftShadow,
} from './FilterTags.style';
import CloseIcon from '../../assets/icons/CloseIcon';
import { INTIAL_FILTERS, USED_COMBINED_FILTERS_LIST_TIER_3 } from '../../constants/initialFilters';
import { filtersClear, filterCurrentState, filterClear } from '../../utils/analyticsEventListeners';
import { device } from '../../styles/devices';
import { useScrollHorizontTracker } from '../../hooks/UI/useScrollTracker';
import useAnimations from '../../hooks/Animations/useAnimations';
import useFilterTagProcessor from '../../hooks/Filters/useFilterTagProcessor';
import RemoveFilterModal from './RemoveFilterModal';
import buildQueryParams from '../../hooks/Filters/useUpdateQueryParams';
import useFilterOptions from '../../hooks/Filters/useFilterOptions';

const FilterTags: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterUserWantToRemove, setFilterUserWantToRemove] = useState({
    filterType: '',
    filterValue: '',
  });

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLaptop = useMediaQuery({ query: device.laptop });

  const tagsRef = useRef(null);

  const { confirmedFilters } = useSelector(selectFilters);
  const { isTier3page, tier3Filters, filterGroupsToRender, isUrlParamsDynamic } =
    useSelector(selectAppConfig);

  const { listOfAllFilters } = useFilterOptions();

  const { isStartReached, isEndReached } = useScrollHorizontTracker(tagsRef);

  const { filterTagsToRender, renderNumberRangeFilters, filterCount } = useFilterTagProcessor(
    confirmedFilters,
    listOfAllFilters,
    isTier3page,
  );

  const shouldFilterNonAudi = useMemo(() => {
    if (confirmedFilters.vehicleType === 'new') return false;
    if (
      confirmedFilters.nonAudi === 'foreign-brand.yes' ||
      confirmedFilters.usedInventoryType === 'has-warranties.nonAudi'
    )
      return true;
    if (confirmedFilters.usedInventoryType === 'has-warranties.combined') return true;
    return false;
  }, [confirmedFilters.nonAudi, confirmedFilters.usedInventoryType, confirmedFilters.vehicleType]);

  const { availableInMonths, vehicleType } = confirmedFilters;

  const { partnerDealers } = listOfAllFilters;
  const mainDealer = partnerDealers?.find((option) => option.isMainDealer);

  const shouldShowRemoveAllFilters = !!filterCount;

  const handleRemoveFilter = useCallback(
    (filterType: string, filter: any) => {
      if (!isLaptop && !isModalOpen) {
        setFilterUserWantToRemove({ filterType, filterValue: filter });
        setIsModalOpen(true);
        return;
      }
      filterClear(confirmedFilters, `${filterType}.${filter}`);

      // CHANGE LATER
      dispatch(
        removeFilter({
          filterType,
          filterValue: filterType === 'availableInMonths' ? 3 : filter,
          isTier3page: isTier3page ? 'true' : 'false',
          isUrlParamsDynamic: isUrlParamsDynamic ? 'true' : 'false',
        }),
      );
      setFilterUserWantToRemove({ filterType: '', filterValue: '' });
      if (!isLaptop) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
    },
    [confirmedFilters, dispatch, isLaptop, isModalOpen, isTier3page, isUrlParamsDynamic],
  );

  const resetAllFilters = useCallback(() => {
    if (isTier3page) {
      dispatch(setDefaultTier3Filters(tier3Filters));
      if (isUrlParamsDynamic) {
        buildQueryParams(null);
      }
    }
    if (!isTier3page) dispatch(clearAllFilters());
    // Analytics Events - clear all filters
    filtersClear(confirmedFilters);
    // Analytics Events - clear all filters
    filterCurrentState(isTier3page ? tier3Filters : INTIAL_FILTERS);
    // Reload main dealer id
    if (mainDealer) {
      dispatch(setMainDealerId(mainDealer.dealerId));
    }
    if (!isLaptop) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [
    confirmedFilters,
    dispatch,
    isLaptop,
    isTier3page,
    isUrlParamsDynamic,
    mainDealer,
    tier3Filters,
  ]);

  const resetFiltersComponent = useMemo(() => {
    if (!shouldShowRemoveAllFilters) return null;
    if (isTier3page && isEqual(confirmedFilters, tier3Filters)) return null;
    return (
      <ResetFiltersCTA>
        <ResetLabel onClick={resetAllFilters}>{t('vlp.vehicleResults.resetFilters')}</ResetLabel>
        {isLaptop && <FilterCount>({filterCount})</FilterCount>}
      </ResetFiltersCTA>
    );
  }, [
    confirmedFilters,
    filterCount,
    isLaptop,
    isTier3page,
    resetAllFilters,
    shouldShowRemoveAllFilters,
    t,
    tier3Filters,
  ]);

  const newAvailabilityTag = useMemo(() => {
    if (vehicleType === 'used') return null;
    return (
      <FilterTag>
        <Label>
          {t(`vlp.filters.filterOptions.vehicleType.newTag`)}
          {t(
            `vlp.filters.filterOptions.newInventoryType.availableInMonthsOptions.availableInMonths${availableInMonths}.label`,
          )}
        </Label>
      </FilterTag>
    );
  }, [availableInMonths, t, vehicleType]);

  const inventoryTypeTag = useMemo(() => {
    const isNewVehicle = vehicleType === 'new';
    if (isNewVehicle) return null;
    const typeValue = confirmedFilters.usedInventoryType.split('.')[1];
    if (confirmedFilters.preused?.length) return null;
    const usedLabel =
      typeValue === 'yes'
        ? `${listOfAllFilters.usedInventoryType[typeValue]?.label} only`
        : listOfAllFilters.usedInventoryType[
            typeValue as keyof typeof listOfAllFilters.usedInventoryType
          ]?.label;

    return (
      <FilterTag>
        <Label>
          {t(`vlp.filters.filterOptions.vehicleType.preownedTag`)}
          {usedLabel}
        </Label>
      </FilterTag>
    );
  }, [vehicleType, confirmedFilters, listOfAllFilters, t]);

  const { rightShadowAppearance } = useAnimations(isEndReached);
  const { leftShadowAppearance } = useAnimations(isStartReached);

  return (
    <TagsWrapper>
      <FilterTagWrapper ref={tagsRef}>
        {!isLaptop && <LeftShadow style={leftShadowAppearance} />}
        {!isLaptop && resetFiltersComponent}
        {inventoryTypeTag}
        {newAvailabilityTag}
        {filterTagsToRender.map((filter) => {
          const { label, filterType, value, showType, preLabel, isRemovable } = filter;
          if (filterType === 'partnerDealers') return null;
          if (shouldFilterNonAudi && !USED_COMBINED_FILTERS_LIST_TIER_3.includes(filterType))
            return null;
          return (
            <FilterTag key={`${filterType}+${label}`}>
              <Label>
                {showType && `${preLabel}:`} {label}
              </Label>
              {isRemovable && (
                <IconBox
                  aria-label={`remove ${label} ${filterType} filter`}
                  onClick={() => handleRemoveFilter(filterType, value)}
                >
                  <CloseIcon size={12} />
                </IconBox>
              )}
            </FilterTag>
          );
        })}
        {renderNumberRangeFilters.map((filter) => {
          const { label, filterType, value, showType, preLabel, isRemovable } = filter;
          return (
            <FilterTag key={`${filterType}+${label}`}>
              <Label>
                {showType && `${preLabel}:`} {label}
              </Label>
              {isRemovable && (
                <IconBox
                  aria-label={`remove ${label} ${filterType} filter`}
                  onClick={() => handleRemoveFilter(filterType, value)}
                >
                  <CloseIcon size={12} />
                </IconBox>
              )}
            </FilterTag>
          );
        })}
        {isLaptop && resetFiltersComponent}
        {!isLaptop && <RightShadow style={rightShadowAppearance} />}
      </FilterTagWrapper>
      {isModalOpen && (
        <RemoveFilterModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          filterUserWantToRemove={filterUserWantToRemove}
          handleRemoveFilter={handleRemoveFilter}
        />
      )}
    </TagsWrapper>
  );
};

export default FilterTags;
